// Differentiator #3: Auftrag in 3 Aktionen, nicht in 3 SAP-Reitern. (1) welches Geraet,
// (2) was ist kaputt (auto-vorgeschlagen aus frueheren Meldungen), (3) bestaetigen — alles
// uebrige kommt aus den Profil-Defaults.
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/NewOrderWizard.jsx). Geaendert
// wurden NUR Styling-Idiom (v4-Token/.gx-* -> isDarkMode-Verzweigung mit konkreten
// Tailwind-v3-Klassen), Theme-/i18n-Hook, i18n-Keys (pm.-Praefix) und die Import-/API-Pfade.
// Verhalten, Props und Struktur bleiben identisch.
//
// MONOLITH-API-KONVENTION (wie SearchBar.js): `user` ist das ERSTE Argument jeder API-Funktion
// (traegt das JWT) und die Funktion liefert den geparsten JSON-Body DIREKT zurueck — KEIN
// { data }-Wrapper wie im Prototyp. Daher `res?.items` statt `({ data }) => data.items` und
// `res.ok` statt `({ data }) => data.ok`. `user` wird zusaetzlich zu `me` durchgereicht:
// `me` traegt das Profil (me.profile.scope), `user` traegt die Auth — auch an SearchBar weiter.
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Wrench, CheckCircle2 } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { equipmentNotifications, createOrder, listTemplates, saveTemplate, useTemplate as markTemplateUsed } from '../api';
import { PRIORITIES, ORDER_STEP_KEYS, SAP_SHORT_TEXT_MAX, SAP_LONG_TEXT_MAX } from '../constants';
import { Button, Card, Row, StepDots } from './ui';
import PlantSelect from './PlantSelect';
import RecentEquipmentList from './RecentEquipmentList';
import SapPayloadPanel from './SapPayloadPanel';
import SearchBar from './SearchBar';
import VoiceInput from './VoiceInput';

// Auftragsart-Label, drei Quellen in fester Reihenfolge:
//   1. i18n 'pm.orderType.<code>' (uebersetzte Baseline-Codes)
//   2. das Kunden-Mapping aus /me.orderTypes (general_settings 'pm_order_types' — DIE Stelle,
//      an der ein neuer Kunde seine Auftragsarten benennt, ohne Code-Aenderung)
//   3. der rohe SAP-Code (fremdes Customizing ohne Pflege — ehrlich statt versteckt)
const orderTypeLabel = (t, code, me, language) => {
  const key = `pm.orderType.${code}`;
  const label = t(key);
  if (label !== key) return `${label} (${code})`;
  const cfg = (me?.orderTypes || []).find((o) => o.code === code);
  if (cfg) {
    const name = language === 'de' ? cfg.labelDe : cfg.labelEn;
    if (name && name !== code) return `${name} (${code})`;
  }
  return code;
};

function NewOrderWizard({ me, user, seedEquipment, onPlantChange, onDone, onCancel, onOpenOrder }) {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const scope = me.profile.scope;
  const [equipment, setEquipment] = useState(seedEquipment || null);
  const [step, setStep] = useState(seedEquipment ? 2 : 1);
  const [problem, setProblem] = useState('');
  const [longText, setLongText] = useState('');
  const [priority, setPriority] = useState('3-mittel');
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Ein Idempotency-Key pro Wizard-Session: ein Doppeltipp oder Retry darf nie ein Duplikat anlegen.
  const idemKey = useRef(crypto.randomUUID());
  // Vorlagen (Slice a): geladen fuer Schritt 1; Anwenden fuellt die Felder und springt so weit
  // wie moeglich (mit Equipment -> direkt zu Schritt 3, ohne -> Geraet zuerst waehlen).
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState('');
  const [tplShared, setTplShared] = useState(false);
  const [tplSaveState, setTplSaveState] = useState(null); // null | 'saving' | 'done' | 'error'

  // Auftragsart ist waehlbar (Details-Panel in Schritt 3), wenn das Profil mehrere erlaubt.
  // Default bleibt PM03 (Reparatur — der typische "Problem melden"-Fall), sonst die erste im Scope.
  // Default: die im Kunden-Mapping als isDefault markierte Art, sofern das Profil sie erlaubt;
  // sonst die bisherige Heuristik (PM03 = "Problem melden", sonst erste erlaubte).
  const cfgDefault = (me.orderTypes || []).find((o) => o.isDefault && scope.orderTypes.includes(o.code));
  const [orderType, setOrderType] = useState(
    cfgDefault ? cfgDefault.code : (scope.orderTypes.includes('PM03') ? 'PM03' : scope.orderTypes[0])
  );
  const plant = me.activePlant;
  const workCenter = equipment?.workCenter || scope.workCenters[0];

  useEffect(() => {
    if (!equipment) return;
    let alive = true;
    // Monolith-API: `user` zuerst, Antwort ist direkt der JSON-Body (.items).
    // Vorschlaege sind optional — bei Fehlern bleibt die Chip-Zeile einfach weg.
    equipmentNotifications(user, equipment.equipmentId, me.profile.id)
      .then((res) => { if (alive) setSuggestions((res?.items || []).map((n) => n.description)); })
      .catch(() => { if (alive) setSuggestions([]); });
    return () => { alive = false; };
  }, [equipment, user, me.profile.id]);

  useEffect(() => {
    let alive = true;
    listTemplates(user, me.activePlant, me.profile.id)
      .then((res) => { if (alive) setTemplates(res?.items || []); })
      .catch(() => { if (alive) setTemplates([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, me.activePlant]);

  const applyTemplate = (tpl) => {
    setProblem(tpl.description || '');
    setLongText(tpl.longText || '');
    if (tpl.priority) setPriority(tpl.priority);
    if (tpl.orderType && scope.orderTypes.includes(tpl.orderType)) setOrderType(tpl.orderType);
    if (tpl.equipmentId) {
      setEquipment({ equipmentId: tpl.equipmentId, description: tpl.equipmentDesc || tpl.equipmentId });
      setStep(3);
    }
    // Nutzung zaehlen - reines Sortier-Signal, Fehler bewusst verschluckt.
    markTemplateUsed(user, tpl.id, me.profile.id).catch(() => {});
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) return;
    setTplSaveState('saving');
    try {
      const res = await saveTemplate(user, {
        name: tplName.trim(),
        shared: tplShared,
        orderType,
        plant,
        equipmentId: equipment?.equipmentId,
        equipmentDesc: equipment?.description,
        description: problem.trim(),
        longText: longText.trim() || undefined,
        priority,
      }, me.profile.id);
      setTplSaveState(res?.ok === false ? 'error' : 'done');
    } catch {
      setTplSaveState('error');
    }
  };

  const submit = async () => {
    setSubmitting(true);
    const body = {
      orderType, plant, workCenter, equipmentId: equipment.equipmentId,
      description: problem.trim(), priority,
      longText: longText.trim() || undefined,
    };
    // pmCall gibt Business-Failures ({ok:false, error}) ZURUECK (Screens zeigen result.error) und
    // wirft nur bei Transport-/Serverfehlern — daher try/catch fuer den Wurf-Fall.
    try {
      const data = await createOrder(user, body, idemKey.current, me.profile.id);
      // Business-Failure: SAP hat sicher NICHTS angelegt und der Nutzer aendert vor dem Retry
      // vermutlich den Payload -> neuer Key (gleicher Key + anderer Payload verletzt den Kontrakt).
      // Bei GEWORFENEN Fehlern (Netz/Timeout) bleibt der Key stehen: SAP koennte gebucht haben,
      // genau dafuer ist der idempotente Retry da.
      if (data && data.ok === false) idemKey.current = crypto.randomUUID();
      setResult(data);
    } catch (e) {
      setResult({ ok: false, error: e?.message || 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.ok) {
    return (
      <SuccessScreen
        result={result}
        onDone={onDone}
        onOpenOrder={onOpenOrder}
        // Mechaniker (canAddComponents) bekommen den Teile-Wortlaut, andere neutral "Auftrag oeffnen".
        openLabel={me.profile.functionRights.canAddComponents ? t('pm.order.addPartsNow') : t('pm.order.openCreated')}
      />
    );
  }

  return (
    <div>
      {/* Kopfzeile: Zurueck links (Schritt 1 -> Startseite, sonst ein Schritt), Werk rechts —
          der Auftrag wird im AKTIVEN Werk angelegt, darum ist es hier sicht- und waehlbar. */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <button
          onClick={step === 1 ? onCancel : () => setStep(step - 1)}
          className={`flex items-center gap-1 font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
        >
          <ArrowLeft size={18} /> {t('pm.common.back')}
        </button>
        <PlantSelect me={me} onPlantChange={onPlantChange} />
      </div>
      <StepDots step={step} total={3} labels={ORDER_STEP_KEYS.map((k) => t(k))} />

      {step === 1 && (
        <section className="space-y-3">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {t('pm.order.whichDevice')}
          </h2>
          <SearchBar user={user} role={me.profile.id} autoFocus onSelect={(eq) => { setEquipment(eq); setStep(2); }} />
          {/* Zuletzt verwendete Geraete des Users (bis 10, aktives Werk) — ein Tipp statt Suche. */}
          <RecentEquipmentList user={user} me={me} max={10} onSelect={(eq) => { setEquipment(eq); setStep(2); }} />
          {/* Vorlagen: ein Tipp fuellt den Wizard. Mit hinterlegtem Geraet geht es direkt zur
              Bestaetigung; ohne bleibt Schritt 1 (Geraet waehlen), der Rest ist vorbelegt. */}
          {templates.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                {t('pm.tpl.title')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {templates.map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => applyTemplate(tpl)}
                    className={`px-3 py-2 rounded-xl text-sm font-medium border transition-all ${isDarkMode
                      ? 'bg-white/[0.08] text-white/80 border-white/15 hover:border-white/30'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-400'}`}
                  >
                    {tpl.name}
                    {tpl.shared && <span className={`ml-1 text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>{t('pm.tpl.sharedTag')}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {step === 2 && equipment && (
        <section className="space-y-5">
          <EquipmentBanner equipment={equipment} isDarkMode={isDarkMode} />
          <div>
            <label className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.order.problemLabel')}
            </label>
            <div className="mt-2">
              {/* Kurztext (SAP KTEXT, 40 Zeichen) — bewusst OHNE Mikro: Diktat gehoert in die
                  Details unten (Langtext), wo nichts gekappt wird. */}
              <textarea
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                rows={2}
                maxLength={SAP_SHORT_TEXT_MAX}
                placeholder={t('pm.order.problemPlaceholder')}
                className={`w-full outline-none p-4 text-lg rounded-xl border transition-all duration-200 ${isDarkMode
                  ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12] placeholder:text-white/40'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40 placeholder:text-slate-400'
                }`}
              />
            </div>
            <p className={`mt-1 text-right text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
              {problem.length}/{SAP_SHORT_TEXT_MAX}
            </p>
            <label className={`mt-3 block font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.order.longTextLabel')}
            </label>
            <div className="mt-2 relative">
              {/* Langtext (Kontraktfeld longText) — HIER landet das Diktat, ohne 40-Zeichen-Kappung.
                  Ist der Kurztext noch leer, wird er aus dem ersten Diktat vorbelegt (kuerzbar). */}
              <textarea
                value={longText}
                onChange={(e) => setLongText(e.target.value)}
                rows={4}
                maxLength={SAP_LONG_TEXT_MAX}
                placeholder={t('pm.order.longTextPlaceholder')}
                className={`w-full outline-none p-4 rounded-xl border transition-all duration-200 ${isDarkMode
                  ? 'bg-white/[0.08] border-white/15 text-white/80 hover:bg-white/[0.12] placeholder:text-white/40'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-brand/40 placeholder:text-slate-400'
                }`}
              />
              <VoiceInput
                user={user} role={me.profile.id} features={me.features}
                onText={(text) => {
                  setLongText((lt) => `${lt ? `${lt} ` : ''}${text}`.slice(0, SAP_LONG_TEXT_MAX));
                  setProblem((p) => p || text.slice(0, SAP_SHORT_TEXT_MAX));
                }}
              />
            </div>
            {suggestions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`text-sm w-full ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {t('pm.order.pastNotifications')}
                </span>
                {suggestions.map((s, i) => (
                  // Vorschlags-Chip — soft brand-getoent (gx-chip mit brand-Akzent).
                  // slice: uebernommene Meldungstexte respektieren dieselbe Kurztext-Grenze.
                  <button
                    key={i}
                    onClick={() => setProblem(s.slice(0, SAP_SHORT_TEXT_MAX))}
                    className={`text-sm rounded-full px-3 py-1.5 border text-left transition-colors ${isDarkMode
                      ? 'bg-brand/[0.08] text-blue-200 border-brand/40 hover:bg-brand/[0.12]'
                      : 'bg-brand/[0.08] text-brand border-brand/20 hover:bg-brand/[0.12]'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {t('pm.order.priority')}
            </label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {PRIORITIES.map((p) => {
                const active = priority === p.key;
                // Aktiv = brand gefuellt; sonst = sekundaere Oberflaeche (gx-field-artig).
                const cls = active
                  ? (isDarkMode
                      ? 'bg-brand/20 text-blue-200 border-brand/40'
                      : 'bg-brand text-white border-brand')
                  : (isDarkMode
                      ? 'bg-white/[0.08] text-white/80 border-white/15'
                      : 'bg-slate-50 text-slate-700 border-slate-200');
                return (
                  <button
                    key={p.key}
                    onClick={() => setPriority(p.key)}
                    className={`min-h-[52px] rounded-xl font-semibold border-2 transition-all duration-200 ${cls}`}
                  >
                    {t(p.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={() => setStep(3)} disabled={!problem.trim()}>{t('pm.common.next')}</Button>
        </section>
      )}

      {step === 3 && equipment && (
        <section className="space-y-4">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {t('pm.order.confirmTitle')}
          </h2>
          <Card className="space-y-2">
            <Row label={t('pm.order.fieldDevice')} value={`${equipment.description} (${equipment.equipmentId})`} info={t('pm.info.equipment')} />
            <Row label={t('pm.order.fieldProblem')} value={problem} info={t('pm.info.problem')} />
            {longText.trim() && (
              <Row label={t('pm.order.fieldLongText')} value={longText.length > 80 ? `${longText.slice(0, 80)}…` : longText} />
            )}
            <Row label={t('pm.order.fieldPriority')} value={t(PRIORITIES.find((p) => p.key === priority)?.labelKey)} info={t('pm.info.priority')} />
          </Card>

          {/* SAP-Transparenz: exakt der Payload, der an SAP geht — plus Auswahl der Auftragsart,
              wenn das Profil mehrere erlaubt ("Details aendern" kann jetzt wirklich aendern).
              The rows ABOVE are the fields createWorkOrder actually sends; priority and long text
              sit below them, marked as not transmitted (see the comment there). */}
          <SapPayloadPanel
            showLabel={t('pm.order.detailsShow')}
            hideLabel={t('pm.order.detailsHide')}
            note={t('pm.order.defaultsNote')}
            rows={[
              { label: t('pm.order.fieldOrderType'), value: orderTypeLabel(t, orderType, me, language), info: t('pm.info.orderType') },
              { label: t('pm.order.fieldPlant'), value: plant, info: t('pm.info.plant') },
              { label: t('pm.order.fieldWorkCenter'), value: workCenter, info: t('pm.info.workCenter') },
              { label: t('pm.order.fieldDevice'), value: equipment.equipmentId, info: t('pm.info.equipment') },
              { label: t('pm.order.fieldPriority'), value: priority, info: t('pm.info.priority') },
              { label: t('pm.order.fieldLongText'),
                value: longText.trim() ? `${longText.trim().slice(0, 60)}${longText.trim().length > 60 ? '…' : ''}` : undefined },
            ]}
          >
            {/* Both fields reach SAP since 2026-09-03 and are listed among the transmitted rows
                above: priority as `priok` → AFIH-PRIOK, and the long text into the order header
                text (AUFK/KOPF) in the session language, which is also the language the portal's
                audit journal now follows. PM-SAP-FIELD-TRANSPARENCY.md §1.1 and §6.5.1. */}
            {scope.orderTypes.length > 1 && (
              <div className="pt-1">
                <span className={`text-xs font-medium ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  {t('pm.order.fieldOrderType')}
                </span>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {scope.orderTypes.map((code) => {
                    const active = orderType === code;
                    const cls = active
                      ? (isDarkMode ? 'bg-brand/20 text-blue-200 border-brand/40' : 'bg-brand text-white border-brand')
                      : (isDarkMode ? 'bg-white/[0.08] text-white/80 border-white/15' : 'bg-slate-50 text-slate-700 border-slate-200');
                    return (
                      <button
                        key={code}
                        onClick={() => setOrderType(code)}
                        className={`min-h-[40px] rounded-xl text-sm font-semibold border transition-all duration-200 ${cls}`}
                      >
                        {orderTypeLabel(t, code, me, language)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </SapPayloadPanel>

          {/* Als Vorlage speichern: gleiche Felder, wiederverwendbar. Teilen ist serverseitig auf
              canManageUsers begrenzt - die Checkbox erscheint nur dann. */}
          {tplSaveState === 'done' ? (
            <p className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>{t('pm.tpl.saved')}</p>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  value={tplName}
                  onChange={(e) => setTplName(e.target.value)}
                  placeholder={t('pm.tpl.namePlaceholder')}
                  maxLength={80}
                  className={`flex-1 min-w-0 px-3 py-2 rounded-xl text-sm border outline-none ${isDarkMode
                    ? 'bg-white/[0.06] text-white border-white/15 placeholder:text-white/30'
                    : 'bg-white text-slate-800 border-slate-200 placeholder:text-slate-400'}`}
                />
                <button
                  onClick={saveAsTemplate}
                  disabled={!tplName.trim() || !problem.trim() || tplSaveState === 'saving'}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border transition-all disabled:opacity-40 ${isDarkMode
                    ? 'bg-white/[0.08] text-white/80 border-white/15'
                    : 'bg-slate-100 text-slate-700 border-slate-200'}`}
                >
                  {tplSaveState === 'saving' ? t('pm.tpl.saving') : t('pm.tpl.saveBtn')}
                </button>
              </div>
              {me.profile.functionRights.canManageUsers && (
                <label className={`flex items-center gap-2 text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
                  <input type="checkbox" checked={tplShared} onChange={(e) => setTplShared(e.target.checked)} className="rounded" />
                  {t('pm.tpl.shareLabel')}
                </label>
              )}
              {tplSaveState === 'error' && (
                <p className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{t('pm.tpl.saveError')}</p>
              )}
            </div>
          )}

          {result && !result.ok && (
            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
              {t('pm.order.errorPrefix')}: {result.error}
            </p>
          )}
          <Button variant="confirm" onClick={submit} disabled={submitting}>
            {submitting ? t('pm.order.creating') : (<><Check /> {t('pm.order.createBtn')}</>)}
          </Button>
          <p className={`text-center text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
            {t(me.sap?.live ? 'pm.order.createHint' : 'pm.order.createHintMock')}
          </p>
        </section>
      )}
    </div>
  );
}

// Equipment-Banner — soft brand-getoente Karte mit Wrench-Badge (gx-chip/gx-card-Idiom).
function EquipmentBanner({ equipment, isDarkMode }) {
  return (
    <div className={`border rounded-2xl p-3 flex items-center gap-3 ${isDarkMode
      ? 'bg-brand/[0.08] border-brand/40'
      : 'bg-brand/[0.08] border-brand/20'
    }`}>
      <div className={`rounded-xl p-2 ${isDarkMode ? 'bg-brand/20 text-blue-200' : 'bg-brand text-white'}`}>
        <Wrench size={20} />
      </div>
      <div>
        <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          {equipment.description}
        </div>
        <div className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
          {equipment.equipmentId} · {equipment.functionalLocation}
        </div>
      </div>
    </div>
  );
}

// Erfolgs-Screen: gruener Haken, Auftragsnummer prominent, Hinweis bei Idempotenz-Replay.
function SuccessScreen({ result, onDone, onOpenOrder, openLabel }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  return (
    <div className="text-center py-10 space-y-4">
      <CheckCircle2 className={`mx-auto ${isDarkMode ? 'text-emerald-300' : 'text-emerald-500'}`} size={64} />
      <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
        {t('pm.order.successTitle')}
      </h2>
      <p className={isDarkMode ? 'text-white/60' : 'text-slate-500'}>{t('pm.order.orderNumber')}</p>
      <p className={`text-3xl font-mono font-bold ${isDarkMode ? 'text-blue-200' : 'text-brand'}`}>
        {result.orderId}
      </p>
      {result.idempotent && (
        <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
          {t('pm.order.duplicateNote')}
        </p>
      )}
      {/* Kein Sackgassen-Screen: direkter Absprung ins Auftragsdetail — dort sitzen Komponenten
          ("Teile hinzufuegen", canAddComponents) bzw. die Freigabe (Meister). */}
      <div className="pt-4 flex flex-col items-center gap-3">
        {onOpenOrder && (
          <Button onClick={() => onOpenOrder(result.orderId)}>{openLabel}</Button>
        )}
        <button
          onClick={onDone}
          className={`text-sm font-medium underline ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}
        >
          {t('pm.common.done')}
        </button>
      </div>
    </div>
  );
}

export default NewOrderWizard;
